// Naming patterns reserved for auto-generated extraction-derived repos
// ("mini repos" / extract-to-DB / auto-extraction). A user-created repo must
// NOT match these, otherwise deleting a normal repo could cascade-delete a repo
// that only *looks* like its derived child by name.
// Keep in sync with the backend discovery rule in
// server/repo_deletion_service.py (_find_child_repos).
export const RESERVED_EXTRACTION_REPO_SUFFIXES = [
    '_extracted_db',
    '_auto_extracted_db',
    // Auto-extraction "mini repos" are named "<parent>_<topic>_mini"; the
    // backend cascade discovers them by the "_mini" ending, so reserve it.
    '_mini',
];
export const RESERVED_EXTRACTION_REPO_PREFIX = 'db_';

// Returns { isReserved, error } — isReserved=true when `name` collides with the
// extraction-derived repo naming convention (reserved suffixes or prefix).
export const isReservedRepoName = (name) => {
    const result = { isReserved: false, error: null };
    if (!name) {
        return result;
    }
    const normalized = name.trim().toLowerCase();

    const matchedSuffix = RESERVED_EXTRACTION_REPO_SUFFIXES.find((suffix) =>
        normalized.endsWith(suffix)
    );
    if (matchedSuffix) {
        result.isReserved = true;
        result.error = `Repository name can't end with "${matchedSuffix}" — that ending is reserved for auto-generated extraction repositories.`;
        return result;
    }

    if (normalized.startsWith(RESERVED_EXTRACTION_REPO_PREFIX)) {
        result.isReserved = true;
        result.error = `Repository name can't start with "${RESERVED_EXTRACTION_REPO_PREFIX}" — that prefix is reserved for auto-generated extract-to-database repositories.`;
        return result;
    }

    return result;
};

// Single source of truth for "is this repo name acceptable?". Returns { valid, error }.
// `existingNames` is a list (strings or {name}) or an isNameFound-style predicate; []
// skips the collision check. Collision is case/whitespace-insensitive because the server
// treats those as the same repo (the name keys its crawl queue and workspace folder).
export const validateRepoName = (name, existingNames = []) => {
    const valid = { valid: true, error: null };
    const invalid = (error) => ({ valid: false, error });

    const raw = name == null ? '' : String(name);
    const trimmed = raw.trim();

    if (!trimmed) {
        return invalid('Please enter a repository name.');
    }

    // "repo " and "repo" hit the same server folder, so reject rather than silently trim.
    if (raw !== trimmed) {
        return invalid('Repository name cannot start or end with a space.');
    }

    const nameCheck = isValidName(trimmed, false);
    if (!nameCheck.isValid) {
        return invalid(nameCheck.error);
    }

    const reserved = isReservedRepoName(trimmed);
    if (reserved.isReserved) {
        return invalid(reserved.error);
    }

    let taken = false;
    if (typeof existingNames === 'function') {
        try {
            taken = Boolean(existingNames(trimmed));
        } catch (err) {
            taken = false;
        }
    } else {
        const target = trimmed.toLowerCase();
        taken = (existingNames || []).some((entry) => {
            const existing = typeof entry === 'string' ? entry : entry && entry.name;
            return String(existing == null ? '' : existing).trim().toLowerCase() === target;
        });
    }
    if (taken) {
        return invalid('A repository with this name already exists!');
    }

    return valid;
};

// Function to verify if the name is a valid file/folder name.
export const isValidName = (name, isFile) => {
    // Regular expression to check for invalid characters
    const invalidChars = /[\/:*?"<>|]/;
    // Regular expression to check for valid characters and position of periods
    const validFilePattern = /^[^ .][^\/:*?"<>|]{0,253}[^ .]$/;
    const validFolderPattern = /^[^ .][^\/:*?"<>|]{0,254}$/;

    const result = {
        isValid: true,
        error: null,
    };

    // Check for invalid characters
    if (invalidChars.test(name)) {
        result.isValid = false;
        result.error = 'Name contains invalid characters!';
        return result;
    }

    // Check length
    if (name.length < 1) {
        result.isValid = false;
        result.error = 'Name cannot be empty!';
        return result;
    }

    if (name.length > 60) {
        result.isValid = false;
        result.error = 'Name characters length cannot be more than 60.';
        return result;
    }

    // Check for valid pattern
    if (isFile) {
        if (!validFilePattern.test(name)) {
            result.isValid = false;
            result.error = 'Invalid file name format!';
            return result;
        }
    } else {
        if (!validFolderPattern.test(name)) {
            result.isValid = false;
            result.error = 'Invalid directory name format!';
            return result;
        }
    }

    return result;
};
